import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import {
  downloadUploadCsv,
  fetchOrders,
  getLabelJob,
  importOrderExport,
  issueLabels,
  listOrders,
  registerMomoTracking,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import type {
  FetchOrdersResponse,
  LabelIssuanceJobDto,
  OrderExportImportResponse,
} from "@/api/types";

// 2026-06-19: かんたん在庫を 6 番目の store として追加 (Kota さん要望)。
// 顧客は Shopee セラーで、リードタイム短縮のため Shopee と同等以前の優先度。
// 並び順は「優先度」を反映する目的で、かんたん在庫 → Shopee → ... の順に。
const PLATFORMS = ["kantan_zaiko", "shopee", "lazada", "joom", "shopify", "momo"];

// 一括ラベル発行ボタンの対象 (momo / kantan_zaiko は送り状番号入力が必要なため
// 別セクション (ステップ 2b / 2c) で扱う)
const BULK_LABEL_PLATFORMS = ["shopee", "lazada", "joom", "shopify"];

// momo の deliveryCompany 選択肢 (Vendor API 仕様書 §3、主要なもの + その他)
const MOMO_CARRIERS: { value: string; label: string }[] = [
  { value: "Sagawa", label: "佐川急便 (Sagawa)" },
  { value: "ECMS", label: "ECMS" },
  { value: "YMT", label: "ヤマト運輸" },
  { value: "JP", label: "日本郵便" },
  { value: "SEINO", label: "西濃運輸" },
  { value: "FTL", label: "福山通運" },
  { value: "Other", label: "その他" },
];

// 2026-06-19 追加: かんたん在庫の運送会社選択肢 (国内発送、momo より少ない)。
// Kota さんが「Wonect 倉庫 → LOGEX 倉庫」便で実運用するキャリア。
const KANTAN_ZAIKO_CARRIERS: { value: string; label: string }[] = [
  { value: "Sagawa", label: "佐川急便 (Sagawa)" },
  { value: "Yamato", label: "ヤマト運輸 (Yamato)" },
  { value: "JP", label: "日本郵便" },
  { value: "Other", label: "その他" },
];

export default function OrdersFetchPage() {
  const [sinceHours, setSinceHours] = useState(720);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(PLATFORMS);
  const [parallel, setParallel] = useState(true);
  const [result, setResult] = useState<FetchOrdersResponse | null>(null);
  // 非同期ラベル発行ジョブ (2026-06-09)。polling で進捗を更新する。
  const [labelJob, setLabelJob] = useState<LabelIssuanceJobDto | null>(null);
  const labelJobPollRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Shopify SG / Shopee TW 注文情報アップロード (ステップ 1.5、複数店舗ファイル対応)
  const [exportFiles, setExportFiles] = useState<File[]>([]);
  const [exportResult, setExportResult] =
    useState<OrderExportImportResponse | null>(null);
  // momo 専用ラベル発行の入力
  const [selectedMomoOrders, setSelectedMomoOrders] = useState<string[]>([]);
  const [momoCarrier, setMomoCarrier] = useState("Sagawa");
  const [momoTrackingNo, setMomoTrackingNo] = useState("");
  // 2026-06-19 追加: かんたん在庫専用ラベル発行の入力 (momo と同じパターン)
  const [selectedKantanZaikoOrders, setSelectedKantanZaikoOrders] = useState<string[]>([]);
  const [kantanZaikoCarrier, setKantanZaikoCarrier] = useState("Sagawa");
  const [kantanZaikoTrackingNo, setKantanZaikoTrackingNo] = useState("");
  const queryClient = useQueryClient();

  const fetchMut = useMutation({
    mutationFn: () =>
      fetchOrders({
        since_hours: sinceHours,
        platforms: selectedPlatforms,
        parallel,
      }),
    onSuccess: (data) => {
      setResult(data);
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      // 受注取得後は注文一覧 (momo の発行待ち一覧 など) も更新する
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setResult(null);
    },
  });

  const exportMut = useMutation({
    // 複数ファイル (Shopify SG / Shopee TW 各店舗) を順番に取り込み結果を合算する。
    // 突合は注文番号で行うため、店舗ごとに別ファイルでもまとめてアップロードできる。
    mutationFn: async (files: File[]) => {
      const results: OrderExportImportResponse[] = [];
      for (const f of files) {
        results.push(await importOrderExport(f));
      }
      const merged: OrderExportImportResponse = {
        source: results.length === 1 ? results[0].source : undefined,
        rows_read: results.reduce((s, r) => s + r.rows_read, 0),
        orders_in_file: results.reduce((s, r) => s + r.orders_in_file, 0),
        orders_updated: results.reduce((s, r) => s + r.orders_updated, 0),
        unmatched_orders: results.flatMap((r) => r.unmatched_orders),
        updated: results.flatMap((r) => r.updated),
        errors: results.flatMap((r) => r.errors),
      };
      return merged;
    },
    onSuccess: (data) => {
      setExportResult(data);
      setError(null);
      // 受取人情報が変わるので一覧・ダッシュボードを更新
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setExportResult(null);
    },
  });

  const labelsMut = useMutation({
    // momo は送り状番号入力が必要なため一括対象から除外 (別セクションで発行)
    mutationFn: () => issueLabels({ platforms: BULK_LABEL_PLATFORMS }),
    onSuccess: (data) => {
      // 即座に job_id が返るので、polling 用 state に置く (useEffect で polling)
      setLabelJob(data);
      setError(null);
      if (!data.id) {
        // 対象 0 件のとき
        queryClient.invalidateQueries({ queryKey: ["dashboard"] });
        queryClient.invalidateQueries({ queryKey: ["orders"] });
      }
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setLabelJob(null);
    },
  });

  // ラベル発行ジョブの進捗を polling (3秒間隔)。完了したら停止+invalidate。
  useEffect(() => {
    // 既存の polling を一旦停止
    if (labelJobPollRef.current !== null) {
      window.clearInterval(labelJobPollRef.current);
      labelJobPollRef.current = null;
    }
    if (!labelJob?.id) return;
    const isFinal = (s?: string) =>
      s === "completed" || s === "failed" || s === "canceled";
    if (isFinal(labelJob.status)) return;
    const id = labelJob.id;
    const intervalId = window.setInterval(async () => {
      try {
        const updated = await getLabelJob(id);
        setLabelJob(updated);
        if (isFinal(updated.status)) {
          window.clearInterval(intervalId);
          labelJobPollRef.current = null;
          queryClient.invalidateQueries({ queryKey: ["dashboard"] });
          queryClient.invalidateQueries({ queryKey: ["orders"] });
          queryClient.invalidateQueries({ queryKey: ["orders", "momo", "new"] });
        }
      } catch (e) {
        // 通信失敗等は表示せずに polling を継続 (一時的なネットワーク断は許容)
        // 致命的なら次のレスポンスで status=failed が来るはず
        console.warn("label job polling failed:", e);
      }
    }, 3000);
    labelJobPollRef.current = intervalId;
    return () => {
      window.clearInterval(intervalId);
      labelJobPollRef.current = null;
    };
  }, [labelJob?.id, labelJob?.status, queryClient]);

  // momo の NEW 注文一覧 (ラベル発行対象の選択用)
  const momoOrdersQuery = useQuery({
    queryKey: ["orders", "momo", "new"],
    queryFn: () => listOrders({ platform: "momo", status: "new", limit: 200 }),
  });

  // 2026-06-24 新運用: 朝のステップ 2b は「送り状番号を DB に登録するだけ」
  // (momo API は叩かない=Shipped 化しない)。実出荷時 (出荷実績 CSV 取込) に
  // 非同期ジョブが Shipped 化 + ラベル取得を自動実行する。
  // 旧: issueLabels({momo_delivery: {...}}) で同期発行 → 廃止
  const momoLabelsMut = useMutation({
    mutationFn: () =>
      registerMomoTracking({
        order_ids: selectedMomoOrders,
        delivery_company: momoCarrier,
        delivery_no: momoTrackingNo.trim(),
      }),
    onSuccess: (data) => {
      setError(null);
      setSelectedMomoOrders([]);
      setMomoTrackingNo("");
      // 注文一覧を refetch (登録後も momo 注文は status=new のまま、
      // tracking_no と delivery_company が埋まる)。
      queryClient.invalidateQueries({ queryKey: ["orders", "momo", "new"] });
      // 登録結果メッセージを表示 (LabelIssuanceJob ではない単発レスポンス)
      alert(data.message);
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
    },
  });

  const toggleMomoOrder = (id: string) => {
    setSelectedMomoOrders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // 2026-06-19 追加: かんたん在庫の NEW 注文一覧 + ラベル発行 (momo と同じパターン)
  const kantanZaikoOrdersQuery = useQuery({
    queryKey: ["orders", "kantan_zaiko", "new"],
    queryFn: () =>
      listOrders({ platform: "kantan_zaiko", status: "new", limit: 200 }),
  });

  const kantanZaikoLabelsMut = useMutation({
    mutationFn: () =>
      issueLabels({
        order_ids: selectedKantanZaikoOrders,
        momo_delivery: {
          delivery_company: kantanZaikoCarrier,
          delivery_no: kantanZaikoTrackingNo.trim(),
        },
      }),
    onSuccess: (data) => {
      setLabelJob(data);
      setError(null);
      setSelectedKantanZaikoOrders([]);
      setKantanZaikoTrackingNo("");
    },
    onError: (e) => {
      setError(e instanceof ApiError ? e.message : String(e));
      setLabelJob(null);
    },
  });

  const toggleKantanZaikoOrder = (id: string) => {
    setSelectedKantanZaikoOrders((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const csvMut = useMutation({
    // ラベル発行後 (status=label_ready) の注文を対象にする。
    // 受注取得直後 (status=new) はまだ tracking_no が無いため、ここで CSV にすると
    // 追跡番号が空欄になってしまう。ラベル発行 → CSV の業務フローに合わせる。
    // only_unexported=true: まだ CSV に出していない分だけ (毎回純粋に新規分)。
    // mark_exported=true: 出力分に「出した」印を付ける。再出力はどちらも false。
    // platforms: 指定なし=全部、["shopee"]等 で platform 単独 DL (2026-06-11)
    mutationFn: (opts: {
      only_unexported: boolean;
      mark_exported: boolean;
      platforms?: string[];
      country_codes?: string[];
      exclude_country_codes?: string[];
      filename_label?: string;
    }) =>
      downloadUploadCsv({
        status: "label_ready",
        one_row_per_item: true,
        only_unexported: opts.only_unexported,
        mark_exported: opts.mark_exported,
        platforms: opts.platforms,
        country_codes: opts.country_codes,
        exclude_country_codes: opts.exclude_country_codes,
        filename_label: opts.filename_label,
      }),
    onSuccess: ({ blob, filename }) => {
      // ブラウザでダウンロードトリガ
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? "airlogi_upload.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      // exported マークが変わるので一覧を更新
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const togglePlatform = (p: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-brand-primary">受注取得</h2>
        <p className="text-sm text-gray-500 mt-1">
          各プラットフォームから受注を取得し、エアロジへアップロードする CSV を生成します。
        </p>
      </div>

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">ステップ 1. 受注取得</h3>
        <div>
          <label className="label">取得期間（過去N時間、デフォルト 720h = 30日）</label>
          <input
            type="number"
            className="input w-32"
            value={sinceHours}
            min={1}
            max={720}
            onChange={(e) => setSinceHours(Number(e.target.value))}
          />
          <p className="text-xs text-gray-500 mt-1">
            週末を挟む業務に対応するためデフォルト 720h (30日)。momo API の最大範囲。
          </p>
        </div>
        <div>
          <label className="label">対象プラットフォーム</label>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <label
                key={p}
                className={`px-3 py-1 rounded border cursor-pointer text-sm ${
                  selectedPlatforms.includes(p)
                    ? "bg-brand-accent text-white border-brand-accent"
                    : "bg-white text-gray-700 border-gray-300"
                }`}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={selectedPlatforms.includes(p)}
                  onChange={() => togglePlatform(p)}
                />
                {p}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="inline-flex items-center text-sm">
            <input
              type="checkbox"
              checked={parallel}
              onChange={(e) => setParallel(e.target.checked)}
              className="mr-2"
            />
            並列実行（高速）
          </label>
        </div>
        <button
          className="btn-primary"
          onClick={() => fetchMut.mutate()}
          disabled={fetchMut.isPending || selectedPlatforms.length === 0}
        >
          {/* 2026-07-01 追加: 選択中の platform 名をボタンに表示。
              全 platform 選択が既定なので、「momo だけのつもり」で押しても
              実際は全 platform 対象になる誤操作を UI 上で防ぐ (6/30 事例)。 */}
          {fetchMut.isPending
            ? "取得中…"
            : selectedPlatforms.length === PLATFORMS.length
              ? `受注を取得 (全 ${PLATFORMS.length} platform)`
              : `受注を取得 (${selectedPlatforms.join(", ")})`}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded">
          {error}
        </div>
      )}

      {result && (
        <div className="card space-y-3">
          <h3 className="text-lg font-semibold">取得結果</h3>
          <p className="text-sm">
            合計 <strong className="text-brand-primary text-lg">{result.total_fetched}</strong> 件取得しました
          </p>
          {(result.cancelled_orders?.length ?? 0) > 0 && (
            <div className="bg-red-50 border-2 border-red-300 text-red-800 p-4 rounded space-y-2">
              <p className="font-bold">
                ⚠ プラットフォーム側でキャンセルされた注文を検知しました
                ({result.cancelled_orders!.length} 件) — 印刷・発送対象から自動除外済み
              </p>
              <ul className="text-sm space-y-1">
                {result.cancelled_orders!.map((c) => (
                  <li key={c.order_no}>
                    <span className="font-mono">{c.order_no}</span>
                    <span className="ml-1 text-xs">({c.platform_status})</span>
                    <span className="block text-xs text-red-700 ml-2">{c.note}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(result.cancel_sweep_errors?.length ?? 0) > 0 && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-2">
              キャンセル照合の一部が失敗しました (照合できなかった分は次回の受注取得で再照合されます):
              {" "}{result.cancel_sweep_errors!.join(" / ")}
            </div>
          )}
          <table className="min-w-full text-sm">
            <thead className="border-b text-left text-gray-500">
              <tr>
                <th className="py-2 pr-4">店舗</th>
                <th className="py-2 pr-4 text-right">取得件数</th>
                <th className="py-2">ステータス</th>
              </tr>
            </thead>
            <tbody>
              {result.results.map((r) => (
                <tr key={r.shop} className="border-b last:border-0">
                  <td className="py-2 pr-4 font-mono text-xs">{r.shop}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{r.fetched}</td>
                  <td className="py-2">
                    {r.error ? (
                      <span className="text-red-600 text-xs">{r.error}</span>
                    ) : (
                      <span className="text-green-600">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">
          ステップ 1.5. Shopify SG / Shopee TW 注文情報アップロード（該当注文がある回のみ）
        </h3>
        <p className="text-sm text-gray-500">
          <strong>Shopify SG（kotofuku-sg）</strong> と <strong>Shopee TW</strong> は、
          プラットフォーム側の制約で氏名・住所・電話などを API から取得できません
          （Shopee は常に <code>****</code> でマスク）。各管理画面の注文エクスポートを
          アップロードすると、注文番号で突合して受取人情報を補完します。
          <br />
          ・<strong>Shopify SG</strong>: 管理画面の注文エクスポート <strong>CSV</strong>（orders_export.csv）
          <br />
          ・<strong>Shopee TW</strong>: Seller Center の注文エクスポート <strong>xlsx</strong>（Order.toship.*.xlsx）
          <br />
          <strong>ステップ 1（受注取得）の後、ステップ 2（ラベル発行）の前</strong>に実行してください。
          形式（CSV / xlsx）は自動判別します。該当注文が無い回はスキップで構いません。
          <br />
          <strong>店舗が複数ある場合（例: Shopee TW 2 店舗）は、各店舗のエクスポートを
          まとめて選択</strong>できます（ファイル選択時に複数選択、または順番にアップロードでも可）。
        </p>
        <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded text-xs">
          更新するのは受取人情報（氏名・住所・郵便番号・電話など）のみです。注文ステータスやラベルには影響しません。
          受注取得をもう一度押しても、ここで補完した情報は保持されます。
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="file"
            multiple
            accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) =>
              setExportFiles(e.target.files ? Array.from(e.target.files) : [])
            }
            className="text-sm"
          />
          <button
            className="btn-accent"
            onClick={() => {
              if (exportFiles.length === 0) {
                setError("注文エクスポート（CSV / xlsx）を選択してください");
                return;
              }
              exportMut.mutate(exportFiles);
            }}
            disabled={exportMut.isPending || exportFiles.length === 0}
          >
            {exportMut.isPending
              ? "アップロード中…"
              : exportFiles.length > 1
                ? `注文情報をアップロード（${exportFiles.length} ファイル）`
                : "注文情報をアップロード"}
          </button>
        </div>

        {exportResult && (
          <div className="space-y-2 pt-2">
            <p className="text-sm">
              {exportResult.source === "shopee"
                ? "Shopee"
                : exportResult.source === "shopify"
                  ? "Shopify"
                  : "注文"}
              エクスポート：ファイル内 <strong>{exportResult.orders_in_file}</strong> 件 / 更新{" "}
              <strong className="text-green-700">{exportResult.orders_updated}</strong> 件 / 未突合{" "}
              <strong className="text-amber-700">
                {exportResult.unmatched_orders.length}
              </strong>{" "}
              件
            </p>
            {exportResult.updated.length > 0 && (
              <table className="min-w-full text-sm">
                <thead className="border-b text-left text-gray-500">
                  <tr>
                    <th className="py-2 pr-4">注文番号</th>
                    <th className="py-2 pr-4">ステータス</th>
                    <th className="py-2">更新項目</th>
                  </tr>
                </thead>
                <tbody>
                  {exportResult.updated.map((u) => (
                    <tr key={u.order_no} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-mono text-xs">{u.order_no}</td>
                      <td className="py-2 pr-4 text-xs">{u.status}</td>
                      <td className="py-2 text-xs text-gray-600">
                        {u.updated_fields.length > 0
                          ? u.updated_fields.join(", ")
                          : "（変更なし）"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {exportResult.unmatched_orders.length > 0 && (
              <p className="text-xs text-amber-700">
                未突合（先に受注取得が必要）:{" "}
                {exportResult.unmatched_orders.join(", ")}
              </p>
            )}
            {exportResult.errors.length > 0 && (
              <p className="text-xs text-red-600">
                {exportResult.errors.join(" / ")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">ステップ 2. 出荷ラベル発行 (momo 以外)</h3>
        <p className="text-sm text-gray-500">
          <strong>Shopee / Lazada / Joom / Shopify(ECMS)</strong> の{" "}
          <code>status=new</code> 受注に対して、各キャリア API で出荷ラベル + 追跡番号を発行します。
          発行後、対象注文は <code>label_ready</code> に進み、エアロジ CSV に追跡番号が反映されます。
          <br />
          <strong>momo は送り状番号の入力が必要なため、下のステップ 2b で発行してください。</strong>
        </p>
        <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded text-xs space-y-1">
          <p className="font-semibold">⚠️ プラットフォーム別の影響 (押す前に必ず確認):</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li><strong>ECMS</strong> (Shopee TW + Shopify): ECMS shipment 作成、お客様への影響なし</li>
            <li><strong>Shopee non-TW</strong>: ship_order でステータスが PROCESSED に進む (tracking 発行)</li>
            <li><strong>Lazada</strong>: ステータスが <code>To Pack → To Handover</code> に進む (<strong>不可逆</strong>)。お客様への発送通知メールはまだ送信されない</li>
            <li><strong>Joom</strong>: ステータスが <code>approved → shipped</code> に変わり、<strong>お客様に発送通知メールが送信される</strong></li>
          </ul>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            if (labelsMut.isPending) return;
            const ok = window.confirm(
              "出荷ラベルを発行します (momo 以外)。\n\n" +
              "この操作は取り消せません。\n\n" +
              "各プラットフォームへの影響:\n" +
              "・ Lazada: ステータスが To Pack → To Handover (不可逆)\n" +
              "・ Joom: ステータスが approved → shipped、お客様に発送通知メールが届きます\n" +
              "・ ECMS / Shopee: tracking 発行 (お客様への影響軽微)\n\n" +
              "梱包・発送する準備が整っていることを確認してから実行してください。\n\n" +
              "本当に実行しますか?"
            );
            if (!ok) return;
            labelsMut.mutate();
          }}
          disabled={labelsMut.isPending}
        >
          {labelsMut.isPending ? "発行中…" : "出荷ラベル発行 (momo 以外)"}
        </button>
      </div>

      {/* ラベル発行ジョブの進捗 UI。ステップ 2 と 2b の間に置くことで、両方の
          ボタン (非 momo / momo) を押した直後に視認できる位置にする。
          (2026-06-12 要望: 旧来はページ最下部にあったため非 momo ボタン押下時に
           進捗が見えない UX 課題があった) */}
      {labelJob && (
        <div className="card space-y-3">
          <h3 className="text-lg font-semibold">出荷ラベル発行ジョブ</h3>
          {labelJob.note && (
            <p className="text-sm text-gray-600">{labelJob.note}</p>
          )}
          {labelJob.id && (
            <>
              <p className="text-xs text-gray-500 font-mono">
                Job ID: {labelJob.id}
              </p>
              {(() => {
                const total = labelJob.total ?? 0;
                const processed = labelJob.processed ?? 0;
                const success = labelJob.success_count ?? 0;
                const failed = labelJob.failed_count ?? 0;
                const percent = total > 0 ? Math.round((processed / total) * 100) : 0;
                const status = labelJob.status ?? "queued";
                const isRunning = status === "queued" || status === "running";
                const statusLabel: Record<string, string> = {
                  queued: "待機中",
                  running: "実行中",
                  completed: "完了",
                  failed: "失敗",
                  canceled: "キャンセル",
                };
                const statusColor: Record<string, string> = {
                  queued: "bg-gray-100 text-gray-700",
                  running: "bg-blue-100 text-blue-800",
                  completed:
                    failed > 0
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-green-100 text-green-800",
                  failed: "bg-red-100 text-red-800",
                  canceled: "bg-gray-100 text-gray-700",
                };
                return (
                  <>
                    <div className="flex items-center gap-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs ${statusColor[status]}`}
                      >
                        {statusLabel[status] ?? status}
                      </span>
                      <span className="text-sm tabular-nums">
                        {processed}/{total} 件
                        {isRunning && " 処理中…"}
                      </span>
                      <span className="text-sm text-green-700 tabular-nums">
                        成功 {success}
                      </span>
                      {failed > 0 && (
                        <span className="text-sm text-red-700 tabular-nums">
                          失敗 {failed}
                        </span>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded h-3 overflow-hidden">
                      <div
                        className={`h-3 transition-all ${
                          failed > 0 ? "bg-yellow-500" : "bg-green-500"
                        }`}
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                    {!isRunning && (
                      <p className="text-xs text-gray-500">
                        完了しました。注文一覧で結果を確認してください。
                      </p>
                    )}
                  </>
                );
              })()}
              {labelJob.error_summary && labelJob.error_summary.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-red-700">
                    エラー ({labelJob.error_summary.length} 件)
                  </p>
                  <table className="min-w-full text-sm">
                    <thead className="border-b text-left text-gray-500">
                      <tr>
                        <th className="py-2 pr-4">注文番号</th>
                        <th className="py-2 pr-4">プラットフォーム</th>
                        <th className="py-2">エラー内容</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labelJob.error_summary.map((e, i) => (
                        <tr
                          key={`${e.order_id ?? i}-${i}`}
                          className="border-b last:border-0"
                        >
                          <td className="py-2 pr-4 font-mono text-xs">
                            {e.order_no ?? e.order_id ?? "-"}
                          </td>
                          <td className="py-2 pr-4 text-xs">{e.platform ?? "-"}</td>
                          <td className="py-2 text-red-600 text-xs">
                            {e.error ?? "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">ステップ 2b. momo 送り状番号登録 (運送会社 + 送り状番号)</h3>
        <p className="text-sm text-gray-500">
          momo は日本国内運送会社 (現在は佐川急便) で momo 倉庫へ送ります。
          <strong>同じ荷物にまとめる注文を選択し、運送会社と送り状番号を 1 つ入力</strong>してください。
          選択した注文すべてに同じ送り状番号が登録されます。
        </p>
        <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded text-xs">
          ℹ️ 2026-06-24 新運用: このボタンでは <strong>送り状番号を DB に登録するだけ</strong>で、
          momo 側のステータスはまだ <strong>変わりません</strong>。実出荷時 (出荷実績 CSV 取込時) に
          自動で「Shipped 化 + 出荷ラベル取得」が走ります。これにより、購入者に「Shipped と表示
          されているのに追跡できない」状態が無くなります。
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">運送会社</label>
            <select
              className="input w-48"
              value={momoCarrier}
              onChange={(e) => setMomoCarrier(e.target.value)}
            >
              {MOMO_CARRIERS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">送り状番号</label>
            <input
              type="text"
              className="input w-64"
              placeholder="例: 佐川の送り状番号"
              value={momoTrackingNo}
              onChange={(e) => setMomoTrackingNo(e.target.value)}
            />
          </div>
        </div>

        <div className="card overflow-x-auto">
          {momoOrdersQuery.isLoading ? (
            <p className="text-gray-500 py-3">momo 注文を読み込み中…</p>
          ) : (momoOrdersQuery.data?.orders.length ?? 0) === 0 ? (
            <p className="text-center text-gray-400 py-3">
              発行待ち (status=new) の momo 注文はありません
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="border-b text-left text-gray-500">
                <tr>
                  <th className="py-2 pr-3">
                    <input
                      type="checkbox"
                      checked={
                        (momoOrdersQuery.data?.orders.length ?? 0) > 0 &&
                        selectedMomoOrders.length === momoOrdersQuery.data?.orders.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedMomoOrders(
                            (momoOrdersQuery.data?.orders ?? []).map((o) => o.id),
                          );
                        } else {
                          setSelectedMomoOrders([]);
                        }
                      }}
                    />
                  </th>
                  <th className="py-2 pr-3">注文番号</th>
                  <th className="py-2 pr-3">受取人</th>
                  <th className="py-2 pr-3">国</th>
                  <th className="py-2">注文日</th>
                </tr>
              </thead>
              <tbody>
                {(momoOrdersQuery.data?.orders ?? []).map((o) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={selectedMomoOrders.includes(o.id)}
                        onChange={() => toggleMomoOrder(o.id)}
                      />
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{o.order_no}</td>
                    <td className="py-2 pr-3 truncate max-w-xs">{o.recipient_name}</td>
                    <td className="py-2 pr-3">{o.country_code?.toUpperCase()}</td>
                    <td className="py-2 text-xs text-gray-500">
                      {o.order_date ? new Date(o.order_date).toLocaleString("ja-JP") : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <button
          className="btn-primary"
          onClick={() => {
            if (momoLabelsMut.isPending) return;
            if (selectedMomoOrders.length === 0) {
              setError("momo 注文を 1 件以上選択してください");
              return;
            }
            if (!momoTrackingNo.trim()) {
              setError("送り状番号を入力してください");
              return;
            }
            const carrierLabel =
              MOMO_CARRIERS.find((c) => c.value === momoCarrier)?.label ?? momoCarrier;
            const ok = window.confirm(
              `momo 注文に送り状番号を登録します。\n\n` +
              `選択注文: ${selectedMomoOrders.length} 件\n` +
              `運送会社: ${carrierLabel}\n` +
              `送り状番号: ${momoTrackingNo.trim()}\n\n` +
              `この時点では momo 側のステータスはまだ変わりません。\n` +
              `実出荷時 (出荷実績 CSV 取込時) に自動で発送処理されます。\n\n` +
              `本当に登録しますか?`
            );
            if (!ok) return;
            momoLabelsMut.mutate();
          }}
          disabled={momoLabelsMut.isPending}
        >
          {momoLabelsMut.isPending
            ? "登録中…"
            : `選択した momo 注文に送り状番号を登録 (${selectedMomoOrders.length} 件)`}
        </button>
      </div>

      {/* 2026-06-19 追加: ステップ 2c. かんたん在庫ラベル発行 (momo と同じパターン) */}
      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">
          ステップ 2c. かんたん在庫 ラベル発行 (運送会社 + 送り状番号)
        </h3>
        <p className="text-sm text-gray-500">
          かんたん在庫は国内発送 (LOGEX 倉庫宛) で、選択した注文を
          <strong>1 つの段ボールにまとめて佐川急便/ヤマト等で発送</strong>します。
          <strong>同じ荷物にまとめる注文を選択し、運送会社と送り状番号を 1 つ入力</strong>
          してください。選択した注文すべてに同じ送り状番号が登録されます。
        </p>
        <div className="bg-amber-50 border border-amber-200 text-amber-900 p-3 rounded text-xs">
          ⚠️ 発行すると Wonect 内で <strong>label_ready</strong> に進みます。
          F-008 発送完了処理を実行すると、かんたん在庫側の Order が
          <strong>「発送済み」に変わり、顧客に発送通知メールが送信される</strong>ため、
          物理発送が完了してから F-008 を実行してください。
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="label">運送会社</label>
            <select
              className="input w-48"
              value={kantanZaikoCarrier}
              onChange={(e) => setKantanZaikoCarrier(e.target.value)}
            >
              {KANTAN_ZAIKO_CARRIERS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">送り状番号</label>
            <input
              type="text"
              className="input w-64"
              placeholder="例: 佐川/ヤマトの送り状番号"
              value={kantanZaikoTrackingNo}
              onChange={(e) => setKantanZaikoTrackingNo(e.target.value)}
            />
          </div>
        </div>

        <div className="card overflow-x-auto">
          {kantanZaikoOrdersQuery.isLoading ? (
            <p className="text-gray-500 py-3">かんたん在庫 注文を読み込み中…</p>
          ) : (kantanZaikoOrdersQuery.data?.orders.length ?? 0) === 0 ? (
            <p className="text-center text-gray-400 py-3">
              発行待ち (status=new) のかんたん在庫 注文はありません
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="border-b text-left text-gray-500">
                <tr>
                  <th className="py-2 pr-3">
                    <input
                      type="checkbox"
                      checked={
                        (kantanZaikoOrdersQuery.data?.orders.length ?? 0) > 0 &&
                        selectedKantanZaikoOrders.length ===
                          kantanZaikoOrdersQuery.data?.orders.length
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedKantanZaikoOrders(
                            (kantanZaikoOrdersQuery.data?.orders ?? []).map((o) => o.id),
                          );
                        } else {
                          setSelectedKantanZaikoOrders([]);
                        }
                      }}
                    />
                  </th>
                  <th className="py-2 pr-3">注文番号</th>
                  <th className="py-2 pr-3">テナント名</th>
                  <th className="py-2">注文日</th>
                </tr>
              </thead>
              <tbody>
                {(kantanZaikoOrdersQuery.data?.orders ?? []).map((o) => (
                  <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-2 pr-3">
                      <input
                        type="checkbox"
                        checked={selectedKantanZaikoOrders.includes(o.id)}
                        onChange={() => toggleKantanZaikoOrder(o.id)}
                      />
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{o.order_no}</td>
                    <td className="py-2 pr-3 truncate max-w-xs">{o.recipient_name}</td>
                    <td className="py-2 text-xs text-gray-500">
                      {o.order_date ? new Date(o.order_date).toLocaleString("ja-JP") : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <button
          className="btn-primary"
          onClick={() => {
            if (kantanZaikoLabelsMut.isPending) return;
            if (selectedKantanZaikoOrders.length === 0) {
              setError("かんたん在庫 注文を 1 件以上選択してください");
              return;
            }
            if (!kantanZaikoTrackingNo.trim()) {
              setError("送り状番号を入力してください");
              return;
            }
            const carrierLabel =
              KANTAN_ZAIKO_CARRIERS.find((c) => c.value === kantanZaikoCarrier)?.label
              ?? kantanZaikoCarrier;
            const ok = window.confirm(
              `かんたん在庫 ラベルを発行します。\n\n` +
              `選択注文: ${selectedKantanZaikoOrders.length} 件\n` +
              `運送会社: ${carrierLabel}\n` +
              `送り状番号: ${kantanZaikoTrackingNo.trim()}\n\n` +
              `選択した注文すべてに同じ送り状番号が登録され、\n` +
              `Wonect 内で label_ready に進みます。\n\n` +
              `本当に実行しますか?`
            );
            if (!ok) return;
            kantanZaikoLabelsMut.mutate();
          }}
          disabled={kantanZaikoLabelsMut.isPending}
        >
          {kantanZaikoLabelsMut.isPending
            ? "発行中…"
            : `選択した かんたん在庫 注文を発行 (${selectedKantanZaikoOrders.length} 件)`}
        </button>
      </div>

      <div className="card space-y-4">
        <h3 className="text-lg font-semibold">ステップ 3. エアロジ用 CSV ダウンロード</h3>
        <p className="text-sm text-gray-500">
          <strong>ステップ 2 でラベル発行済み (status=label_ready)</strong> の受注を
          CSV でダウンロードし、エアロジ画面からアップロードしてください。
          ラベル発行で追跡番号 (tracking_no) が確定するため、必ずステップ 2 の後に実行してください。
        </p>
        <div className="bg-blue-50 border border-blue-200 text-blue-900 p-3 rounded text-xs">
          下のボタンは<strong>まだ CSV に出していない注文だけ</strong>を出力し、出した注文に
          印を付けます（次回からは新規分だけが出ます）。過去にエクスポート済みの注文は混ざりません。
        </div>
        <p className="text-xs text-gray-600">
          下のボタンは <strong>プラットフォーム単位</strong> でダウンロードできます。
          選んだ platform の注文だけ出力 + その platform 分だけ「エクスポート済み」印が
          付きます。他 platform は未マークのまま残ります。
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          {/* 2026-06-19: かんたん在庫 を先頭に配置 (Kota さん要望)。
              顧客は Shopee セラーで Wonect から仕入れる流れのため、Shopee と
              同等以前の優先度で処理する。 */}
          <button
            className="btn-accent"
            onClick={() => csvMut.mutate({
              only_unexported: true, mark_exported: true,
              platforms: ["kantan_zaiko"],
            })}
            disabled={csvMut.isPending}
            title="かんたん在庫 の label_ready 注文 (未エクスポート分のみ) を CSV 出力"
          >
            かんたん在庫 分
          </button>
          {/* Shopee は TW (ECMS 経由) と他 (自社物流) で運用が異なるため別ボタン
              (2026-06-11 Kota さん要望) */}
          <button
            className="btn-accent"
            onClick={() => csvMut.mutate({
              only_unexported: true, mark_exported: true,
              platforms: ["shopee"], exclude_country_codes: ["tw"],
            })}
            disabled={csvMut.isPending}
            title="Shopee (TW 以外、自社物流) の label_ready 注文を CSV 出力"
          >
            Shopee 分
          </button>
          <button
            className="btn-accent"
            onClick={() => csvMut.mutate({
              only_unexported: true, mark_exported: true,
              platforms: ["shopee"], country_codes: ["tw"],
            })}
            disabled={csvMut.isPending}
            title="Shopee TW (ECMS 経由) の label_ready 注文を CSV 出力"
          >
            Shopee TW 分
          </button>
          {(["lazada", "joom", "shopify", "momo"] as const).map((p) => (
            <button
              key={p}
              className="btn-accent"
              onClick={() => csvMut.mutate({
                only_unexported: true, mark_exported: true, platforms: [p],
              })}
              disabled={csvMut.isPending}
              title={`${p} の label_ready 注文 (未エクスポート分のみ) を CSV 出力`}
            >
              {p.charAt(0).toUpperCase() + p.slice(1)} 分
            </button>
          ))}
          <button
            className="btn-accent"
            onClick={() => csvMut.mutate({
              only_unexported: true, mark_exported: true,
            })}
            disabled={csvMut.isPending}
            title="全 platform の label_ready 注文 (未エクスポート分のみ) を CSV 出力"
          >
            {csvMut.isPending ? "生成中…" : "全て"}
          </button>
        </div>
        <div className="pt-1">
          <button
            className="text-xs text-gray-500 underline"
            onClick={() => {
              const ok = window.confirm(
                "label_ready の注文を全部もう一度出力します（エクスポート印は変更しません）。\n" +
                "前回のCSVをやり直したいとき用です。エアロジは重複注文を弾くので問題ありません。\n\n" +
                "実行しますか?"
              );
              if (!ok) return;
              csvMut.mutate({
                only_unexported: false, mark_exported: false,
                filename_label: "全て_再出力",
              });
            }}
            disabled={csvMut.isPending}
          >
            全 label_ready を再出力（印を変えない・再アップロード用、全 platform）
          </button>
        </div>
      </div>
    </div>
  );
}
